// apps/web/src/components/dashboard/OperationalPulseStrip.tsx
//
// KAN-209 Briefing Desk — "Operational pulse": the eight dashboard
// concepts (T1–T8) as one compact metrics strip instead of a card wall.
// Each metric reuses its existing /api/dashboard/<endpoint> contract via
// useDashboardTile (React Query dedupes against the detail tiles below).
//
// Items are <li>, not <article>, so the §3.1.1 tile lint rule does not
// apply; each item carries exactly one help button and no link — the
// click-through targets live on the richer detail tiles.

import type { ReactNode } from 'react'
import { HelpCircle } from 'lucide-react'
import { useDashboardTile } from '@/hooks/useDashboardTile'
import { displayMapper } from '@/lib/display-mapper'
import { formatEur } from '@/lib/format-eur'
import { Skeleton } from '@/components/ui/skeleton'
import { TrendDelta } from './TrendDelta'
import type { RegulatoryTileResponse } from '@/hooks/useRegulatoryQueries'

interface T1Response {
  count24h: number
  sevenDayAvg: number
  delta: number
}
interface T2Response {
  total: number
  topLine: Array<{ countryIso2: string; sector: string; count: number }>
  delta: number
}
interface T3Response {
  count: number
  breakdown: { kev: number; exploited: number; high: number }
}
interface T4Response {
  count: number
  totalAmountEur: number
  deltaEur: number
  largest: { organisation: string; amountEur: number } | null
}
interface T5Response {
  band: 'red' | 'amber' | 'green'
  ratio: number
  ratioDelta: number
  sector: string | null
}
interface T6Response {
  count: number
  delta: number
  breakdown: { critical: number; high: number; medium: number; low: number }
}
interface T7Response {
  sufficient: boolean
  buckets: Array<{ countryIso2: string; count: number }>
  fallbackSectors: Array<{ sector: string; count: number }> | null
}

const BAND_LABEL: Record<T5Response['band'], string> = {
  red: 'High',
  amber: 'Elevated',
  green: 'Normal',
}

const BAND_CLASSES: Record<T5Response['band'], string> = {
  red: 'text-destructive',
  amber: 'text-amber-700 dark:text-amber-400',
  green: 'text-emerald-700 dark:text-emerald-400',
}

interface MetricContent {
  value: ReactNode
  aside?: ReactNode
}

function MetricItem(props: {
  tileId: string
  label: string
  isLoading: boolean
  isError: boolean
  content: MetricContent | null
  onHelpClick?: (id: string) => void
}) {
  let body: ReactNode
  if (props.isLoading) {
    body = (
      <span role="status" aria-label={`Loading ${props.label}`}>
        <Skeleton className="mt-1 h-6 w-14" />
      </span>
    )
  } else if (props.isError || !props.content) {
    body = (
      <span
        className="text-lg font-heading font-semibold text-muted-foreground"
        title="Failed to load"
      >
        —
      </span>
    )
  } else {
    body = (
      <span className="flex flex-wrap items-baseline gap-2">
        <span className="text-lg font-heading font-semibold leading-tight">
          {props.content.value}
        </span>
        {props.content.aside}
      </span>
    )
  }

  return (
    <li className="group relative min-w-0 bg-card p-3">
      <span className="block truncate pr-5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {props.label}
      </span>
      {body}
      <button
        type="button"
        onClick={() => props.onHelpClick?.(props.tileId)}
        aria-label={`Explain ${props.label}`}
        className="absolute right-1.5 top-1.5 rounded-md p-1 text-muted-foreground opacity-0 transition-opacity duration-100 hover:bg-accent hover:text-accent-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-focus-within:opacity-100 group-hover:opacity-100 motion-reduce:transition-none"
      >
        <HelpCircle className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </li>
  )
}

function CriticalsMetric({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<T1Response>('criticals-24h')
  return (
    <MetricItem
      tileId="t1"
      label="Criticals 24h"
      isLoading={isLoading}
      isError={isError}
      onHelpClick={onHelpClick}
      content={
        data
          ? {
              value: data.count24h.toLocaleString('en-GB'),
              aside: <TrendDelta value={data.delta} polarity="bad-up" />,
            }
          : null
      }
    />
  )
}

function RansomMetric({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<T2Response>('ransom-claims-7d')
  return (
    <MetricItem
      tileId="t2"
      label="Ransom claims 7d"
      isLoading={isLoading}
      isError={isError}
      onHelpClick={onHelpClick}
      content={
        data
          ? {
              value: data.total.toLocaleString('en-GB'),
              aside: <TrendDelta value={data.delta} polarity="bad-up" />,
            }
          : null
      }
    />
  )
}

function PatchesMetric({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<T3Response>('patches-due')
  return (
    <MetricItem
      tileId="t3"
      label="Patches due"
      isLoading={isLoading}
      isError={isError}
      onHelpClick={onHelpClick}
      content={
        data
          ? {
              value: data.count.toLocaleString('en-GB'),
              aside: (
                <span className="whitespace-nowrap font-mono text-xs font-medium text-destructive">
                  {data.breakdown.kev} KEV
                </span>
              ),
            }
          : null
      }
    />
  )
}

function FinesMetric({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<T4Response>('new-fines-30d')
  return (
    <MetricItem
      tileId="t4"
      label="New fines 30d"
      isLoading={isLoading}
      isError={isError}
      onHelpClick={onHelpClick}
      content={
        data
          ? {
              value: data.count.toLocaleString('en-GB'),
              aside: (
                <span className="whitespace-nowrap font-mono text-xs font-medium text-muted-foreground">
                  {formatEur(data.totalAmountEur)}
                </span>
              ),
            }
          : null
      }
    />
  )
}

function SectorHeatMetric({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<T5Response>('sector-heat')
  return (
    <MetricItem
      tileId="t5"
      label="My sector heat"
      isLoading={isLoading}
      isError={isError}
      onHelpClick={onHelpClick}
      content={
        data
          ? {
              value: (
                <span className={BAND_CLASSES[data.band]}>
                  {BAND_LABEL[data.band]}
                </span>
              ),
              aside: (
                <span className="whitespace-nowrap font-mono text-xs font-medium text-muted-foreground">
                  {data.ratio.toFixed(2)}×
                </span>
              ),
            }
          : null
      }
    />
  )
}

function SupplyChainMetric({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<T6Response>('supply-chain-7d')
  return (
    <MetricItem
      tileId="t6"
      label="Supply-chain 7d"
      isLoading={isLoading}
      isError={isError}
      onHelpClick={onHelpClick}
      content={
        data
          ? {
              value: data.count.toLocaleString('en-GB'),
              aside: <TrendDelta value={data.delta} polarity="bad-up" />,
            }
          : null
      }
    />
  )
}

function VictimsMetric({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<T7Response>(
    'victim-countries-30d',
  )
  let content: MetricContent | null = null
  if (data) {
    if (data.sufficient && data.buckets.length > 0) {
      content = {
        value: (
          <span className="text-base">
            {displayMapper.country(data.buckets[0].countryIso2)}
          </span>
        ),
      }
    } else if (data.fallbackSectors && data.fallbackSectors.length > 0) {
      content = {
        value: (
          <span className="text-base">
            {displayMapper.sector(data.fallbackSectors[0].sector)}
          </span>
        ),
      }
    } else {
      content = { value: <span className="text-muted-foreground">—</span> }
    }
  }
  return (
    <MetricItem
      tileId="t7"
      label="Top victims 30d"
      isLoading={isLoading}
      isError={isError}
      onHelpClick={onHelpClick}
      content={content}
    />
  )
}

function RegulatoryMetric({ onHelpClick }: MetricProps) {
  const { data, isLoading, isError } = useDashboardTile<RegulatoryTileResponse>(
    'regulatory-tile',
  )
  let content: MetricContent | null = null
  if (data) {
    if (data.mode === 'personalised') {
      content = {
        value: data.watchedFrameworks.length.toLocaleString('en-GB'),
        aside: (
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            watched
          </span>
        ),
      }
    } else if (data.mode === 'default') {
      content =
        data.deadlines.length > 0
          ? {
              value: data.deadlines.length.toLocaleString('en-GB'),
              aside: (
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  upcoming
                </span>
              ),
            }
          : {
              value: data.frameworks.length.toLocaleString('en-GB'),
              aside: (
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  frameworks
                </span>
              ),
            }
    } else {
      content = { value: <span className="text-base">Set up</span> }
    }
  }
  return (
    <MetricItem
      tileId="t8"
      label="Regulatory watch"
      isLoading={isLoading}
      isError={isError}
      onHelpClick={onHelpClick}
      content={content}
    />
  )
}

interface MetricProps {
  onHelpClick?: (id: string) => void
}

export interface OperationalPulseStripProps {
  onHelpClick?: (id: string) => void
}

export function OperationalPulseStrip(props: OperationalPulseStripProps) {
  return (
    <ul
      aria-label="Operational pulse"
      className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-4 xl:grid-cols-8"
    >
      <CriticalsMetric onHelpClick={props.onHelpClick} />
      <RansomMetric onHelpClick={props.onHelpClick} />
      <PatchesMetric onHelpClick={props.onHelpClick} />
      <FinesMetric onHelpClick={props.onHelpClick} />
      <SectorHeatMetric onHelpClick={props.onHelpClick} />
      <SupplyChainMetric onHelpClick={props.onHelpClick} />
      <VictimsMetric onHelpClick={props.onHelpClick} />
      <RegulatoryMetric onHelpClick={props.onHelpClick} />
    </ul>
  )
}
